import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Copy, Loader2, Plug } from 'lucide-react';
import api from '@/shared/api/client';

/**
 * Étape « brancher la batterie » : les cinq valeurs à saisir dans l'app S-Miles.
 *
 * Une batterie Hoymiles ne parle à personne tant qu'un humain ne lui a pas donné
 * l'adresse de notre broker, sur son téléphone, dans l'app du fabricant
 * (Système → MQTT Service). Ce geste existait déjà — il se transmettait de vive voix ou
 * par capture d'écran, avec un mot de passe unique partagé par tout le parc. Autrement
 * dit chaque poseur repartait avec de quoi écouter, et publier sur, la télémétrie de
 * toutes les autres installations.
 *
 * Désormais l'application crée un compte propre à CETTE batterie, dont les droits ne
 * portent que sur ses propres topics, et l'affiche ici. Le mot de passe est servi par
 * le backend, jamais écrit dans cette page.
 *
 * Les libellés restent en anglais parce que ce sont ceux de l'app S-Miles : le poseur
 * doit reconnaître le champ à l'écran, et un libellé traduit lui ferait chercher le
 * mauvais. La colonne de droite est ce qu'il recopie, à l'identique.
 *
 * ⚠️ Cet écran s'affiche AVANT la vérification, pas après : une batterie qui n'a jamais
 * été réglée ne publiera jamais, et la vérification ne pourrait donner qu'un échec.
 * Depuis le 27/08/2026 cette vérification part toute seule, sans bouton : la phrase
 * ci-dessous ne renvoie donc plus à un geste, mais à un moment.
 */

// L'ordre est celui de l'écran S-Miles, de haut en bas. Le respecter évite au poseur
// de chercher : il descend les deux listes en parallèle.
// Le troisieme est le piege, et il a deja coute. `Client Prefix` vaut MSA et RIEN
// D'AUTRE : l'app S-Miles colle ce prefixe devant le numero de serie pour fabriquer le
// nom sous lequel la machine s'annonce. Recopier l'`Username` (MSA + numero) dedans
// produit MSA + numero + numero, et le broker refuse.
const CHAMPS = [
  ['Server Address', 'server_address'],
  ['Port', 'port'],
  ['Client Prefix', 'client_prefix', 'Ce préfixe seul, sans le numéro de série.'],
  ['Enable Authentication', 'enable_authentication'],
  ['Username', 'username'],
  ['Password', 'password'],
];

function Valeur({ valeur, tid }) {
  const [copie, setCopie] = useState(false);
  const texte = valeur === true ? 'ON' : String(valeur);
  const copier = async () => {
    // Le presse-papier n'existe pas sur une origine non sécurisée (une stack de
    // développement en http). Le bouton reste alors sans effet visible plutôt que de
    // lever une erreur — la valeur est de toute façon lisible et recopiable à la main,
    // ce que le poseur fait dans l'app du fabricant.
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(true);
      setTimeout(() => setCopie(false), 1500);
    } catch { /* origine non sécurisée : rien à faire */ }
  };
  return (
    <div className="flex items-center justify-end gap-1.5">
      <span className="font-mono text-[12px] text-neutral-900 break-all" data-testid={tid}>{texte}</span>
      <button type="button" onClick={copier} className="text-neutral-400 hover:text-neutral-700 shrink-0"
              aria-label={`Copier ${texte}`}>
        <Copy className={`w-3.5 h-3.5 ${copie ? 'text-emerald-600' : ''}`} />
      </button>
    </div>
  );
}

export default function ReglageBroker({ serial }) {
  const [reglage, setReglage] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const numero = (serial || '').trim();

  const preparer = async () => {
    setEnCours(true);
    setErreur('');
    try {
      const { data } = await api.post('/enrolement-materiel/reglage-broker',
                                      { numero_serie: numero });
      setReglage(data.reglage);
    } catch (e) {
      // Un réglage qu'on ne peut pas produire doit se DIRE. En servir un approximatif
      // ferait repartir le poseur en croyant avoir terminé, et la batterie resterait
      // muette sans que personne ne sache pourquoi.
      setErreur(e?.response?.data?.detail
        || 'Réglage indisponible : le compte de la batterie n’a pas pu être créé.');
    }
    setEnCours(false);
  };

  if (!numero) return null;

  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-3"
         data-testid="batterie-reglage-broker">
      <p className="text-[11px] font-medium text-violet-700">
        Brancher la batterie : à faire en premier
        <span className="block font-normal text-neutral-500 mt-0.5">
          app S-Miles → Système → MQTT Service
        </span>
      </p>

      {!reglage && (
        <>
          <p className="text-[11px] text-neutral-600">
            Cette batterie a besoin de son propre compte pour publier. Créez-le, puis
            recopiez les valeurs dans l’app du fabricant : tant que ce n’est pas fait,
            la batterie ne répondra pas.
          </p>
          <Button type="button" variant="outline" className="h-10 w-full"
                  disabled={enCours} onClick={preparer}
                  data-testid="batterie-preparer-branchement">
            {enCours
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><Plug className="w-4 h-4 mr-1.5" />Afficher le réglage de cette batterie</>}
          </Button>
        </>
      )}

      {erreur && (
        <p className="text-[11px] text-red-600" data-testid="batterie-reglage-erreur">{erreur}</p>
      )}

      {reglage && (
        <>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 items-center">
            {CHAMPS.map(([libelle, cle, avertissement]) => (
              <div key={cle} className="contents">
                <dt className="text-[11px] text-neutral-500">
                  {libelle}
                  {avertissement && (
                    <span className="block text-[10px] text-amber-700">{avertissement}</span>
                  )}
                </dt>
                <dd><Valeur valeur={reglage[cle]} tid={`reglage-${cle.replace(/_/g, '-')}`} /></dd>
              </div>
            ))}
          </dl>
          {/* CE QUE LA MACHINE DOIT ANNONCER, une fois les six valeurs saisies. C'est
              la seule facon de verifier la saisie sans attendre : si le journal du
              broker montre autre chose, le prefixe a ete rempli avec l'Username. */}
          {reglage.client_id && (
            <p className="text-[10.5px] text-neutral-600" data-testid="reglage-controle-client-id">
              Une fois enregistré, l’appareil doit s’annoncer sous{' '}
              <span className="font-mono font-medium text-neutral-900"
                    data-testid="reglage-client-id">{reglage.client_id}</span>.
              S’il s’annonce sous autre chose, le champ « Client Prefix » contient le
              nom d’utilisateur au lieu du préfixe.
            </p>
          )}
          <p className="text-[10.5px] text-neutral-500">
            Ce mot de passe ne vaut que pour cette batterie. Revenir sur cet écran
            réaffiche le même : il n’y a rien à noter.
          </p>
        </>
      )}
    </div>
  );
}
